<template>
  <div
    ref="treeBranch"
    class="rs-partial-navigation-tree-branch"
    @click="$emit('click')"
  >
    <div class="d-flex align-items-stretch position-relative z-index-1">
      <div class="leaf d-flex align-items-end">
        <div class="flex-fill stem-x" />
      </div>
      <div class="leaf-bottom-stem" />
      <div
        class="branch-content text-truncate flex-grow-1"
        :class="{ active }"
      >
        <Component
          :is="to && !locked ? 'RouterLink' : 'div'"
          class="flex-fill h-100 rs-unstyled d-flex align-items-center prevent-select text-truncate rs-nav-title-link"
          :class="{ 'branch-locked': locked }"
          :to="to"
        >
          <div
            v-if="title"
            class="branch-content-title d-flex align-items-center text-truncate nav-item-title"
          >
            <i :class="iconClasses" />
            <div
              class="flex-grow-1 text-truncate"
              :title="locked && lockMessage ? lockMessage : title"
            >
              {{ title }}
            </div>

            <i v-if="locked" class="fal fa-lock rs-text-warning" />
          </div>
          <slot />
        </Component>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';

/**
 * A "branch" component nested within `<RsPartialNavigationTree>`.
 * Branches are also expandable to nest more `<RsPartialNavigationTrees>`,
 * so their usage is more than just navigation links. They can also act as containers for further nesting.
 */
export default defineComponent({
  props: {
    /**
     * The title!
     */
    title: {
      type: String,
      default: '',
    },

    /**
     * What Icon the tree branch uses.
     */
    icon: {
      type: String,
      default: '',
    },

    /**
     * Icon size as either `sm` or `lg` variant
     */
    iconSize: {
      type: String as PropType<'sm' | 'lg'>,
      default: 'sm',
    },

    /**
     * When nav tree branch is in active state, what icon will be shown in active state.
     * If none is provided, regular icon prop is used.
     */
    activeIcon: {
      type: String,
      default: '',
    },

    /**
     * Synonymous with the `:to` prop that is used by Vue Router's `<RouterLink>` component.
     */
    to: {
      type: [ String, Object ],
      default: null,
    },

    /**
     * Determines active state of the component, which will toggle some classes and present alternate UI.
     */
    active: {
      type: Boolean,
      default: false,
    },

    /**
     * Adds a specific state to navigation that adds a lock icon
     */
    locked: {
      type: Boolean,
      default: false,
    },

    /**
     * Adds a message to the locked state
     */
    lockMessage: {
      type: String,
      default: 'Contact your Rentsync account manager to enable this feature.',
    },
  },
  emits: ['click'],

  computed: {
    /**
     * Returns icon classes based on various prop states (active, activeIcon, etc.)
     */
    iconClasses(): Record<string, boolean> {
      return {
        [this.icon]: !!this.icon || (!!this.activeIcon && this.active),
        [this.activeIcon]: !!this.activeIcon && this.active,
        [`icon-${this.iconSize}`]: true,
        'branch-title-icon mr-2': !!this.icon || (!!this.activeIcon && this.active),
      };
    },
  },
});
</script>

<style lang="scss">
@import '@/styles/navigation.scss';
</style>
